import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { allArticles } from '@/mocks/articles';

function articleContentToHTML(markdown: string): string {
  let html = markdown;
  html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-heading font-bold text-foreground-900 mt-8 mb-3">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">$1</h1>');
  html = html.replace(/^\- (.+)$/gm, '<li class="ml-4 list-disc text-foreground-700 leading-relaxed">$1</li>');
  html = html.replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal text-foreground-700 leading-relaxed">$2</li>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground-900">$1</strong>');
  html = html.replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-accent-400 pl-4 py-2 my-4 bg-accent-50 rounded-r-lg text-foreground-700 text-sm italic">$1</blockquote>');
  html = html.replace(/\n\n/g, '</p><p class="text-foreground-700 leading-relaxed mb-4">');
  html = '<p class="text-foreground-700 leading-relaxed mb-4">' + html + '</p>';
  html = html.replace(/<li class="ml-4 list-disc text-foreground-700 leading-relaxed">(.+?)<\/li>/g, (match) => {
    if (!match.includes('<ul')) {
      return '<ul class="mb-4 space-y-1">' + match + '</ul>';
    }
    return match;
  });
  return html;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = allArticles.find((a) => a.id === Number(id));
  const relatedArticles = allArticles
    .filter((a) => a.categorySlug === article?.categorySlug && a.id !== article?.id)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-16 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-article-line text-3xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-600 mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentHTML = articleContentToHTML(article.content);

  return (
    <>
      <SEOHead
        title={`${article.title} - 黄瓜视频App官方指南`}
        description={article.excerpt}
        keywords={`黄瓜视频app,${article.category},${article.title}`}
        canonicalUrl={`https://www.example.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
                { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.example.com/category/${article.categorySlug}` },
                { '@type': 'ListItem', position: 3, name: article.title },
              ],
            },
            {
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              datePublished: article.date,
              url: `https://www.example.com/article/${article.id}`,
              author: {
                '@type': 'Organization',
                name: '黄瓜视频官方',
              },
              publisher: {
                '@type': 'Organization',
                name: '黄瓜视频',
                url: 'https://www.example.com',
              },
              image: article.coverImage,
            },
          ],
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-16 bg-background-50">
        <article className="w-full max-w-[860px] mx-auto px-4 md:px-6 lg:px-8">
          <nav className="flex items-center mb-6" aria-label="面包屑导航">
            <Link to="/" className="breadcrumb-link">首页</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={`/category/${article.categorySlug}`} className="breadcrumb-link">{article.category}</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}阅读</span>
              <span>·</span>
              <span>黄瓜视频官方</span>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-[300px] md:h-[400px] object-cover object-top"
            />
          </div>

          <div
            className="prose-content text-foreground-800"
            dangerouslySetInnerHTML={{ __html: contentHTML }}
          />

          {relatedArticles.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-300/60 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-[140px] overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                        {rel.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{rel.readTime}阅读</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}